import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {TReportPlan} from "api/reportBuilderApi/types";
import {
	TReportBuilderState,
	TReportDraft,
	TReportJob,
	TReportSection,
	TReportStep,
	TReportTask,
	TReportTaskKey,
} from "store/report-builder/types";

export const reportBuilderInitialState: TReportBuilderState = {
	drafts: [],
	openProjectId: null,
	plan: null,
	planProjectId: null,
	planKey: null,
	planLoading: false,
	planError: null,
	job: null,
};

type TDraftPayload<T> = {projectId: number} & T;

const findDraft = (state: TReportBuilderState, projectId: number) =>
	state.drafts.find((draft) => draft.projectId === projectId);

const reportBuilderSlice = createSlice({
	name: "reportBuilder",
	initialState: reportBuilderInitialState,
	reducers: {
		draftSaved: (state, action: PayloadAction<TReportDraft>) => {
			const others = state.drafts.filter((d) => d.projectId !== action.payload.projectId);
			state.drafts = [...others, action.payload];
		},
		draftDiscarded: (state, action: PayloadAction<number>) => {
			state.drafts = state.drafts.filter((draft) => draft.projectId !== action.payload);
			if (state.openProjectId === action.payload) state.openProjectId = null;
		},
		draftUpdated: (
			state,
			action: PayloadAction<TDraftPayload<{changes: Partial<TReportDraft>}>>,
		) => {
			const draft = findDraft(state, action.payload.projectId);
			if (draft) Object.assign(draft, action.payload.changes);
		},
		sectionUpdated: (
			state,
			action: PayloadAction<
				TDraftPayload<{sectionId: string; changes: Partial<TReportSection>}>
			>,
		) => {
			const section = findDraft(state, action.payload.projectId)?.sections.find(
				(item) => item.id === action.payload.sectionId,
			);
			if (section) Object.assign(section, action.payload.changes);
		},
		stepChanged: (state, action: PayloadAction<TDraftPayload<{step: TReportStep}>>) => {
			const draft = findDraft(state, action.payload.projectId);
			if (draft) draft.step = action.payload.step;
		},
		builderOpened: (state, action: PayloadAction<number>) => {
			state.openProjectId = action.payload;
		},
		builderClosed: (state) => {
			state.openProjectId = null;
		},
		planRequested: (state, action: PayloadAction<string>) => {
			state.planKey = action.payload;
			state.planLoading = true;
			state.planError = null;
		},
		planReceived: (
			state,
			action: PayloadAction<{key: string; projectId: number; plan: TReportPlan}>,
		) => {
			// A slower answer to an older request must not overwrite the newest one
			if (action.payload.key !== state.planKey) return;
			state.plan = action.payload.plan;
			state.planProjectId = action.payload.projectId;
			state.planLoading = false;
		},
		planFailed: (state, action: PayloadAction<{key: string; error: string}>) => {
			if (action.payload.key !== state.planKey) return;
			state.planLoading = false;
			state.planError = action.payload.error;
		},
		jobStarted: (state, action: PayloadAction<TReportJob>) => {
			state.job = action.payload;
		},
		jobTaskUpdated: (
			state,
			action: PayloadAction<{key: TReportTaskKey; changes: Partial<TReportTask>}>,
		) => {
			const task = state.job?.tasks.find((item) => item.key === action.payload.key);
			if (task) Object.assign(task, action.payload.changes);
		},
		jobFinished: (
			state,
			action: PayloadAction<{
				status: TReportJob["status"];
				error?: string;
				fileName?: string;
			}>,
		) => {
			if (!state.job) return;
			state.job.status = action.payload.status;
			state.job.error = action.payload.error ?? null;
			state.job.fileName = action.payload.fileName ?? null;
			state.job.finishedAt = Date.now();
		},
		jobDismissed: (state) => {
			state.job = null;
		},
	},
});

export const {
	draftSaved,
	draftDiscarded,
	draftUpdated,
	sectionUpdated,
	stepChanged,
	builderOpened,
	builderClosed,
	planRequested,
	planReceived,
	planFailed,
	jobStarted,
	jobTaskUpdated,
	jobFinished,
	jobDismissed,
} = reportBuilderSlice.actions;

export default reportBuilderSlice.reducer;
